import { Link, useNavigate } from "@tanstack/react-router";
import { Loader2, Play } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { type AdminApi, ApiError } from "@/api";
import { Badge, Banner, Button, Card, CardHead, Input, Skeleton } from "@/components/ui";
import { cn } from "@/lib/utils";
import type {
  BatchRubric,
  BatchPageDetail,
  BatchRunView,
  TeBaseline,
  TeSpec,
  TestCaseListItem,
} from "@/types";
import { BACK_LINK_CLASS, BackLink } from "@/components/layout/BackLink";
import { lastSearch } from "@/components/layout/lastSearch";
import { shownPageId } from "@/components/test-result/shownPage";
import { TEST_RESULT_DEFAULT_SEARCH, TEST_RESULT_ROUTE } from "@/components/test-result/testResultSearch";
import { readOnlyReasonText, testCaseGate } from "@/components/test-cases/prodCase";
import { compactTime } from "@/utils/format";
import { formatScore } from "@/utils/rubric";
import { type CellTone, cellView, finishedCount, runStats, TONE_CLASS } from "./batchCells";
import { withLiveRun } from "./liveRun";
import { RunLog } from "./RunLog";
import { TcPicker } from "./TcPicker";
import { TeEditor } from "./TeEditor";
import { DeleteBatchPageButton } from "./DeleteBatchPageButton";
import { normalizeSpec, specKey, teLetters, teName } from "./teSpec";

/** Live runs are read every few seconds — one call returns the whole run (C7). */
export const POLL_MS = 3000;

function errorText(err: unknown) {
  return err instanceof Error ? err.message : String(err);
}

async function allActiveCases(api: AdminApi): Promise<TestCaseListItem[]> {
  const out: TestCaseListItem[] = [];
  let cursor: string | null = null;
  // The picker lists every active TC — it is the only place a set is chosen,
  // and "전체" must mean everything listed (§2). Paged under the hood. Every
  // catalog, not dev only: whether a TC can go on a page is the server's
  // per-row `add_to_batch`, not which database answered (see `addableToPage`).
  do {
    const res = await api.listTestCases({ cursor, limit: 100, environment: "all" });
    out.push(...res.items);
    cursor = res.next_cursor ?? null;
  } while (cursor);
  return out;
}

/**
 * What a page may take: active, and allowed onto a page. The gate is the
 * server's grant when it sends one (a bound prod TC can be granted, a dev
 * one refused); without it the legacy rule — dev only — still holds, so a
 * legacy prod row is not offered. Lock enforces it again either way.
 */
function addableToPage(tc: TestCaseListItem) {
  return tc.is_active && testCaseGate(tc, "add_to_batch").allowed;
}

/**
 * One Batch Test page (fe/TEST_CASES_BATCH.md §1 · new artifact).
 *
 * Left: the TC set — picked and then fixed. Right: the TE — a diff from the
 * deployment — and the run. Below: every run on the page.
 *
 * Locking and running do not change the URL (§1.3); they are changes, not
 * moves. A live run is polled until it ends, then the page is re-read so the
 * log and the fixed rows show the verdicts.
 */
export function BatchPageView({
  api,
  pageId,
  reloadKey = 0,
}: {
  api: AdminApi;
  pageId: string;
  reloadKey?: number;
}) {
  const navigate = useNavigate();
  const [page, setPage] = useState<BatchPageDetail | null>(null);
  const [baseline, setBaseline] = useState<TeBaseline | null>(null);
  const [cases, setCases] = useState<TestCaseListItem[] | null>(null);
  const [loadError, setLoadError] = useState("");
  const [notice, setNotice] = useState("");

  const [selection, setSelection] = useState<string[]>([]);
  const [name, setName] = useState("");
  const [locking, setLocking] = useState(false);

  const [spec, setSpec] = useState<TeSpec>({});
  // The rubric this run is scored with — chosen per run, not part of the TE.
  const [rubric, setRubric] = useState<BatchRubric>("v3");
  const [starting, setStarting] = useState(false);
  const [live, setLive] = useState<BatchRunView | null>(null);
  const [justFinished, setJustFinished] = useState<BatchRunView | null>(null);
  /** Another page's run holding the one slot (artifact: frozen, with where). */
  const [elsewhere, setElsewhere] = useState<{ label: string; runId: string } | null>(null);

  const reload = useCallback(async () => {
    const detail = await api.getBatchPage(pageId);
    setPage(detail);
    return detail;
  }, [api, pageId]);

  useEffect(() => {
    let cancelled = false;
    setLoadError("");
    void (async () => {
      try {
        const [detail, base] = await Promise.all([api.getBatchPage(pageId), api.getTeBaseline()]);
        if (cancelled) return;
        setPage(detail);
        setBaseline(base);
        setName(detail.name);
        setSelection(detail.test_case_ids);
        const running = detail.runs.find((r) => r.status === "running");
        if (running) setLive(await api.getBatchRun(running.batch_run_id));
        if (!detail.locked) {
          const list = await allActiveCases(api);
          if (!cancelled) setCases(list);
        }
      } catch (err) {
        if (!cancelled) setLoadError(err instanceof ApiError && err.code === "PAGE_NOT_FOUND"
          ? "삭제되었거나 존재하지 않는 Batch Test 페이지입니다." : errorText(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, pageId]);

  // The header's Reload re-reads the page only — not the TC draft or the TE
  // being edited, which are this screen's work in progress.
  useEffect(() => {
    if (reloadKey) void reload().catch((err) => setNotice(errorText(err)));
  }, [reload, reloadKey]);

  // One batch runs at a time (BE-BATCH-PAGE). If another page holds the slot,
  // say where before the click rather than after a 409. The list names the
  // candidate; the run itself is read to confirm, because reading a run is
  // what settles one nobody is driving any more.
  const checkElsewhere = useCallback(async () => {
    try {
      const { items } = await api.listBatchPages({ limit: 100 });
      const other = items.find((r) => r.page_id !== pageId && r.last_run?.status === "running");
      if (!other?.last_run) return setElsewhere(null);
      const run = await api.getBatchRun(other.last_run.batch_run_id);
      setElsewhere(
        run.status === "running"
          ? { label: `${other.label} · ${other.name}`, runId: run.batch_run_id }
          : null,
      );
    } catch {
      // Unknown is not busy — the run button still gets BE's 409 if it is.
      setElsewhere(null);
    }
  }, [api, pageId]);

  useEffect(() => {
    void checkElsewhere();
  }, [checkElsewhere, reloadKey]);

  // While it runs, look again every few seconds so the button opens when it ends.
  const elsewhereId = elsewhere?.runId ?? null;
  useEffect(() => {
    if (!elsewhereId) return;
    const timer = window.setInterval(() => void checkElsewhere(), POLL_MS);
    return () => window.clearInterval(timer);
  }, [checkElsewhere, elsewhereId]);

  // Poll the live run until it ends. `liveId` keys the effect so a new run
  // restarts it and a finished one stops it.
  const liveId = live?.status === "running" ? live.batch_run_id : null;
  const reloadRef = useRef(reload);
  useEffect(() => {
    reloadRef.current = reload;
  }, [reload]);
  useEffect(() => {
    if (!liveId) return;
    let stopped = false;
    const timer = window.setInterval(() => {
      void (async () => {
        try {
          const run = await api.getBatchRun(liveId);
          if (stopped) return;
          setLive(run);
          if (run.status !== "running") {
            setJustFinished(run);
            await reloadRef.current();
          }
        } catch {
          // A missed poll is not a failed run — the next tick tries again.
        }
      })();
    }, POLL_MS);
    return () => {
      stopped = true;
      window.clearInterval(timer);
    };
  }, [api, liveId]);

  const normalized = useMemo(() => normalizeSpec(spec, baseline), [spec, baseline]);
  const letters = useMemo(() => teLetters(page?.runs ?? []), [page]);
  const specRun = useMemo(
    () => page?.runs.find((r) => specKey(r.te_spec) === specKey(normalized)) ?? null,
    [page, normalized],
  );
  // The newest *finished* run of the TE being edited — its mean is what the
  // summary quotes; a run still going has no settled mean.
  const specDone = useMemo(
    () =>
      page?.runs.find((r) => r.status !== "running" && specKey(r.te_spec) === specKey(normalized)) ??
      null,
    [page, normalized],
  );
  const isLive = Boolean(liveId);
  // Stable between renders so the picker's grouping is not redone each tick.
  const addable = useMemo(() => (cases ?? []).filter(addableToPage), [cases]);

  // Above the content, as on Test Result — history first, the list otherwise.
  const back = (
    <BackLink
      fallback={
        <Link to="/test-cases/batch" className={BACK_LINK_CLASS}>
          ← 목록
        </Link>
      }
    />
  );

  if (loadError) {
    return (
      <div className="grid gap-4">
        {back}
        <Banner tone="error">{loadError}</Banner>
      </div>
    );
  }
  if (!page) {
    return (
      <div className="grid gap-4">
        <Skeleton className="h-20" />
        <Skeleton className="h-80" />
      </div>
    );
  }

  const single = page.mode === "single";
  const listed = new Set(addable.map((c) => c.test_case_id));
  const lockable = selection.filter((id) => listed.has(id));
  // Already in the draft but not addable — shown with why, never dropped
  // silently. Viewing a row and being allowed to add it are separate.
  const picked = new Set(selection);
  const held = (cases ?? [])
    .filter((c) => picked.has(c.test_case_id) && !listed.has(c.test_case_id))
    // A gate that says yes to a retired row still cannot put it on a page.
    .map((tc) => ({ tc, reason: testCaseGate(tc, "add_to_batch").reason ?? readOnlyReasonText("retired") }));
  const need = single ? 1 : 2;

  const saveName = async () => {
    const next = name.trim();
    if (!next || next === page.name) return setName(page.name);
    try {
      setPage(await api.patchBatchPage(page.page_id, { name: next }));
    } catch (err) {
      setNotice(errorText(err));
    }
  };

  const lock = async () => {
    setLocking(true);
    setNotice("");
    try {
      setPage(await api.lockBatchPage(page.page_id, lockable));
    } catch (err) {
      setNotice(errorText(err));
    } finally {
      setLocking(false);
    }
  };

  const run = async () => {
    setStarting(true);
    setNotice("");
    setJustFinished(null);
    try {
      const started = await api.startBatchRun(page.page_id, normalized, rubric);
      setLive(started);
      await reload();
    } catch (err) {
      // 409 names the page already running — the reader needs to know where.
      setNotice(err instanceof ApiError ? err.message : errorText(err));
      void checkElsewhere();
    } finally {
      setStarting(false);
    }
  };

  const firstCase = page.test_cases[0];
  const commitSource =
    page.locked && firstCase?.source_feedback_id
      ? { environment: firstCase.source_environment, feedbackId: firstCase.source_feedback_id }
      : null;
  const shownRun = isLive ? live : specRun;
  const shownCells = isLive ? live?.cells : specRun ? page.cells[specRun.batch_run_id] : undefined;
  const shownLabel = isLive && live ? teName(live.te_spec, baseline) : teName(normalized, baseline);
  const letterList = [...new Set(letters.values())];
  const barRun = isLive ? live : justFinished;
  // The log reads the page, which is re-read only when the run ends; until
  // then the polled run is the newer copy of its own row.
  const logPage = isLive ? withLiveRun(page, live) : page;
  const shownHere = shownPageId(null) === page.page_id;
  const doneAvg = specDone ? runStats(page.cells[specDone.batch_run_id], page.test_case_ids).avg : null;
  const specStatus = specDone
    ? `${letters.get(specKey(normalized)) ?? ""} 로 실행됨 · 평균 ${doneAvg != null ? formatScore(doneAvg) : "—"}`
    : "이 페이지에서는 아직 실행 전";

  return (
    <div className="grid gap-4">
      {back}
      <Card className="flex flex-wrap items-center gap-3 px-4 py-3">
        <div className="grid min-w-0 gap-0.5">
          <div className="text-sm font-semibold">
            {page.label} · {page.name}
          </div>
          <div className="text-xs text-muted-foreground">
            생성 {compactTime(page.created_at)} · {page.created_by_email ?? "—"}
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5">
          <Badge size="compact">{single ? "단일 TC" : "다중 TC"}</Badge>
          <Badge size="compact">
            {page.locked ? `${page.test_case_ids.length} TC${single ? "" : " 고정"}` : "TC 선택 중"}
          </Badge>
          <Badge size="compact">{page.runs.length} 회 실행</Badge>
          <Badge size="compact">TE {letterList.length ? letterList.join(" · ") : "없음"}</Badge>
        </div>
        {/* Results are read in Test Result — this page starts runs. The
            label says whether the Matrix is already on this page (artifact). */}
        {page.locked && page.runs.length > 0 && (
          <Button
            size="sm"
            variant={shownHere ? "ghost" : "outline"}
            className="ml-auto"
            onClick={() =>
              navigate({
                to: TEST_RESULT_ROUTE,
                search: {
                  ...lastSearch(TEST_RESULT_ROUTE, TEST_RESULT_DEFAULT_SEARCH),
                  page: page.page_id,
                  tc: null,
                },
              })
            }
          >
            {shownHere ? "Matrix 표시 중" : "Matrix 에 표시"}
          </Button>
        )}
        <DeleteBatchPageButton api={api} page={page} runCount={page.runs.length}
          running={isLive || page.runs.some((run) => run.status === "running")} disabled={starting || locking}
          onError={setNotice} onDeleted={() => navigate({ to: "/test-cases/batch", replace: true })} />
      </Card>

      {notice && <Banner tone="error">{notice}</Banner>}

      <div className="grid items-start gap-4 lg:grid-cols-2">
        <Card className="min-w-0">
          {!page.locked ? (
            <>
              <CardHead
                title="1 · TC 선택"
                hint={single ? "정확히 1 개 · 행이 하나라 고정으로 지킬 집합이 없다" : "2 개 이상 · 고정하면 이 페이지에서 바꿀 수 없다"}
              />
              <div className="grid gap-3 p-4">
                <Input
                  aria-label="Batch Test 이름"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onBlur={() => void saveName()}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                  }}
                />
                {cases === null ? (
                  <Skeleton className="h-60" />
                ) : (
                  <TcPicker
                    cases={addable}
                    held={held}
                    selected={selection}
                    single={single}
                    onChange={setSelection}
                  />
                )}
                <div className="flex items-center gap-3">
                  <span className="text-2xl font-semibold tabular-nums">{lockable.length}</span>
                  <span className="text-xs text-muted-foreground">
                    TC 선택 · {single ? "1 개 필요" : "2 개 이상 필요"}
                    {selection.length > lockable.length &&
                      ` · 추가할 수 없거나 목록에 없는 ${selection.length - lockable.length} 건은 빠집니다`}
                  </span>
                  <Button
                    size="sm"
                    className="ml-auto"
                    disabled={locking || lockable.length < need || (single && lockable.length !== 1)}
                    onClick={() => void lock()}
                  >
                    {locking && <Loader2 className="animate-spin" />}
                    {single ? "이 TC 로 진행" : "이 TC 로 고정"}
                  </Button>
                </div>
                <p className="rounded-md border border-border bg-surface-subtle px-3 py-2 text-xs text-muted-foreground">
                  {single
                    ? "행이 하나뿐이라 지킬 행 집합은 없습니다. 다만 실행 기록이 가리키는 TC 가 바뀌지 않도록, 다른 TC 는 새 단일 페이지에서 돌립니다."
                    : "고정한 뒤에는 TC 를 바꿀 수 없습니다. 행 집합이 흔들리면 TE 열끼리 비교가 무의미해지기 때문입니다. 다른 묶음이 필요하면 새 페이지를 만드세요."}
                </p>
              </div>
            </>
          ) : (
            <>
              <CardHead
                title={single ? "1 · TC" : "1 · TC · 고정됨"}
                hint={`${page.test_case_ids.length} 건${page.locked_at ? ` · ${compactTime(page.locked_at)} 고정` : ""} · 오른쪽 = ${shownLabel} ${isLive ? "진행" : "결과"}`}
              />
              <ul>
                {page.test_cases.map((tc) => {
                  const view = shownRun ? cellView(shownCells?.[tc.test_case_id]) : { text: "실행 전", tone: "none" as CellTone };
                  return (
                    <li
                      key={tc.test_case_id}
                      className="grid grid-cols-[76px_minmax(0,1fr)_84px] items-center gap-2 border-b border-border px-4 py-2 text-[13px] last:border-b-0"
                    >
                      <span className="truncate text-[11px] text-muted-foreground" title={tc.test_case_id}>
                        {tc.test_case_id.slice(0, 8)}
                      </span>
                      <span className="min-w-0 truncate" title={tc.trigger_preview ?? undefined}>
                        {tc.trigger_preview || tc.title || "—"}
                      </span>
                      <span
                        title={view.title}
                        className={cn(
                          "justify-self-end rounded-md px-1.5 py-0.5 text-center text-[11px] tabular-nums",
                          TONE_CLASS[view.tone],
                        )}
                      >
                        {view.text}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </Card>

        <Card className={cn("min-w-0", !page.locked && "opacity-60")}>
          <CardHead
            title="2 · TE 선택"
            hint={page.locked ? "배포본에서 바꿀 항목만 고른다 · 실행 1 회 = TE 1 개" : "TC 를 고정하면 열린다"}
          />
          <div className="grid gap-3 p-4">
            <TeEditor
              api={api}
              baseline={baseline}
              spec={spec}
              frozen={!page.locked || isLive || starting}
              commitSource={commitSource}
              onChange={setSpec}
              status={page.locked ? specStatus : null}
            />
            <RubricChoice value={rubric} onChange={setRubric} disabled={!page.locked || isLive || starting} />
            <div className="flex items-center gap-3">
              <span className="text-2xl font-semibold tabular-nums">{page.test_case_ids.length}</span>
              <span className="min-w-0 truncate text-xs text-muted-foreground">
                조합 · {page.test_case_ids.length} TC × 1 TE ({shownLabel})
              </span>
              <Button
                size="sm"
                className="ml-auto"
                disabled={!page.locked || isLive || starting || !!elsewhere || page.test_case_ids.length === 0}
                onClick={() => void run()}
              >
                {starting ? <Loader2 className="animate-spin" /> : <Play />}
                {specRun ? "다시 실행" : "실행"}
              </Button>
            </div>
            {elsewhere && !isLive && (
              <p className="rounded-md border border-border bg-surface-subtle px-3 py-2 text-xs text-muted-foreground" role="status">
                {elsewhere.label} 에서 실행 중입니다. 끝나면 여기서 실행할 수 있습니다.
              </p>
            )}
          </div>
          {barRun && (
            <StatusBar
              pageId={page.page_id}
              run={barRun}
              live={isLive}
              label={teName(barRun.te_spec, baseline)}
              letter={letters.get(specKey(barRun.te_spec)) ?? ""}
            />
          )}
        </Card>
      </div>

      <RunLog page={logPage} baseline={baseline} />
    </div>
  );
}

function StatusBar({
  pageId,
  run,
  live,
  label,
  letter,
}: {
  pageId: string;
  run: BatchRunView;
  live: boolean;
  label: string;
  letter: string;
}) {
  const total = Object.keys(run.cells).length || run.test_case_count;
  const done = finishedCount(run.cells);
  return (
    <div className="flex flex-wrap items-center gap-2 border-t border-border px-4 py-2 text-xs" role="status">
      <span
        aria-hidden
        className={cn("size-2 rounded-full", live ? "animate-pulse bg-primary" : "bg-muted-foreground")}
      />
      <span className="font-medium">{live ? "running" : run.status}</span>
      <span className="text-muted-foreground">
        {letter} · {label}
      </span>
      {live ? (
        <>
          <span className="h-1.5 w-32 overflow-hidden rounded-full bg-muted">
            <i className="block h-full bg-primary" style={{ width: `${total ? (done / total) * 100 : 0}%` }} />
          </span>
          <span className="tabular-nums text-muted-foreground">
            {done} / {total}
          </span>
        </>
      ) : (
        <>
          <span className="text-muted-foreground">
            끝났습니다 — 채점은 별도 패스라 점수가 늦게 들어올 수 있습니다.
          </span>
          <Link
            to={TEST_RESULT_ROUTE}
            search={{ ...TEST_RESULT_DEFAULT_SEARCH, page: pageId }}
            className="ml-auto text-primary hover:underline"
          >
            Matrix 에서 보기
          </Link>
        </>
      )}
    </div>
  );
}

const RUBRIC_OPTIONS: { value: BatchRubric; label: string; hint: string }[] = [
  { value: "v3", label: "rubric_v3", hint: "TC 가 된 사유(👎)로 채점" },
  { value: "common_v1", label: "common_rubric", hint: "모든 TC 를 같은 항목으로 채점" },
];

/** Which rubric the run is scored with — one of two, v3 by default (BE `BATCH_RUBRICS`). */
function RubricChoice({
  value,
  onChange,
  disabled,
}: {
  value: BatchRubric;
  onChange: (value: BatchRubric) => void;
  disabled: boolean;
}) {
  return (
    <fieldset className="grid gap-1.5" disabled={disabled}>
      <legend className="mb-1 text-xs text-muted-foreground">채점 rubric</legend>
      <div className="flex flex-wrap gap-2">
        {RUBRIC_OPTIONS.map((option) => (
          <label
            key={option.value}
            className={cn(
              "flex cursor-pointer items-center gap-2 rounded-md border px-3 py-1.5 text-xs",
              value === option.value ? "border-primary bg-primary/5" : "border-border",
              disabled && "cursor-not-allowed opacity-60",
            )}
          >
            <input
              type="radio"
              name="batch-rubric"
              className="accent-primary"
              value={option.value}
              checked={value === option.value}
              onChange={() => onChange(option.value)}
            />
            <span className="font-medium">{option.label}</span>
            <span className="text-muted-foreground">{option.hint}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}
